const PLACEHOLDER_TARGETS = new Set(['', 'consultation', 'target']);
const PLACEHOLDER_CLASSIFICATIONS = new Set([
  '',
  'unclassified',
  '__custom__',
  'custom',
  'for authorized use only',
  'for official use only',
]);
const GENERIC_TARGETS = new Set([
  '',
  'consultation',
  'target',
  'engagement',
  'project',
  'client',
  'elicitation pretexts',
  'elicitation models',
  'elicitation model',
  'targeted elicitation pretexts',
  'proposed elicitation pretexts',
  'pretexts',
  'pretext pack',
  'campaign pack',
  'threat assessment',
  'strategic assessment',
  'intelligence brief',
  'operational brief',
]);
const ORG_NAME_MARKERS = /\b(?:ministry|agency|agencies|corp|corporation|department|university|bank|company|services|security|defense|government|assessment|consultation|project|operation|exercise|engagement)\b/i;

const normalizeToken = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const labelsMatch = (left, right) => {
  const a = normalizeToken(left);
  const b = normalizeToken(right);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

const isGenericTargetName = (value, templateLabel = '') => {
  const normalized = normalizeToken(value);
  if (!normalized) return true;
  if (GENERIC_TARGETS.has(normalized)) return true;
  if (templateLabel && labelsMatch(normalized, templateLabel)) return true;
  if (/^nation[- ]state ttp brief/.test(normalized)) return true;
  if (/^(?:\d+\.\s*)?(?:executive summary|elicitation pretexts?|targeted elicitation pretexts?|proposed elicitation pretexts?)/i.test(normalized)) {
    return true;
  }
  return false;
};

const isUsableTarget = (value, templateLabel = '') => {
  const cleaned = String(value || '').trim();
  if (!cleaned) return false;
  if (PLACEHOLDER_TARGETS.has(normalizeToken(cleaned))) return false;
  return !isGenericTargetName(cleaned, templateLabel);
};

const looksLikePersonName = (value) => {
  const text = String(value || '').trim();
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return false;
  if (ORG_NAME_MARKERS.test(text)) return false;
  if (parts.some((part) => /\d/.test(part))) return false;
  const nameLike = parts.filter((part) => /^[A-Z][a-zA-Z'.-]+$/.test(part)).length;
  return nameLike >= 2;
};

const formatLastNameSubject = (target) => {
  const text = String(target || '').trim();
  if (!text || !looksLikePersonName(text)) return text;
  const parts = text.split(/\s+/).filter(Boolean);
  return `${parts[parts.length - 1]} ${parts.slice(0, -1).join(' ')}`.trim();
};

const resolveClassification = ({
  classification = '',
  customClassification = '',
  reportPayload = null,
}) => {
  let marking = classification === '__custom__'
    ? String(customClassification || '').trim()
    : String(classification || '').trim();

  if (reportPayload && typeof reportPayload === 'object') {
    const payloadMarking = String(reportPayload.classification_marking || '').trim();
    if (payloadMarking && PLACEHOLDER_CLASSIFICATIONS.has(normalizeToken(marking))) {
      marking = payloadMarking;
    }
  }

  if (!marking || PLACEHOLDER_CLASSIFICATIONS.has(normalizeToken(marking))) {
    return 'UNCLASSIFIED';
  }
  return marking;
};

const parseReportTitleParts = (title) => {
  const text = String(title || '').trim();
  if (!text) return ['', ''];
  for (const sep of [' — ', ' – ', ' - ', '—', '–']) {
    if (text.includes(sep)) {
      const [left, right] = text.split(sep, 2);
      return [left.trim(), right.trim()];
    }
  }
  return [text, ''];
};

const extractTargetFromReportTitle = (title, templateLabel = '') => {
  const text = String(title || '').trim();
  if (!text) return '';

  const subjectMatch = text.match(/(?:^|\b)(?:subject|subj)\s*:\s*(.+?)(?:\s*$|\s*(?:\||\/|\n))/i);
  if (subjectMatch && isUsableTarget(subjectMatch[1], templateLabel)) {
    return subjectMatch[1].trim().replace(/[ .\-|]+$/, '');
  }

  if (text.includes(':')) {
    const idx = text.lastIndexOf(':');
    const right = text.slice(idx + 1).trim().replace(/[ .\-|]+$/, '');
    if (isUsableTarget(right, templateLabel)) {
      return right;
    }
  }

  const [titleTarget, titleTemplate] = parseReportTitleParts(text);
  const templateNorm = normalizeToken(templateLabel);
  const titleTemplateNorm = normalizeToken(titleTemplate);
  if (titleTemplate && titleTemplateNorm === templateNorm && isUsableTarget(titleTarget, templateLabel)) {
    return titleTarget;
  }
  if (titleTarget && normalizeToken(titleTarget) === templateNorm && isUsableTarget(titleTemplate, templateLabel)) {
    return titleTemplate;
  }

  const projectMatch = text.match(/(?:^|\b)(?:project|operation|op)\s*:\s*(.+?)(?:\s*$|\s*(?:\||\/|\n))/i);
  if (projectMatch && isUsableTarget(projectMatch[1], templateLabel)) {
    return projectMatch[1].trim().replace(/[ .\-|]+$/, '');
  }

  if (isUsableTarget(titleTarget, templateLabel)) return titleTarget;
  if (isUsableTarget(text, templateLabel)) return text;
  return '';
};

export const resolveExportFilenameParts = ({
  clientName = '',
  projectName = '',
  reportTemplateLabel = '',
  classification = '',
  customClassification = '',
  reportPayload = null,
}) => {
  let client = String(clientName || '').trim();
  let templateLabel = String(reportTemplateLabel || '').trim();
  let target = String(projectName || '').trim();
  const resolvedClassification = resolveClassification({
    classification,
    customClassification,
    reportPayload,
  });

  if (!isUsableTarget(target, templateLabel)) {
    target = '';
  }

  if (reportPayload && typeof reportPayload === 'object') {
    const preparedFor = String(reportPayload.prepared_for || '').trim();
    if ((!client || normalizeToken(client) === 'client') && preparedFor) {
      client = preparedFor;
    }

    const title = String(reportPayload.title || '').trim();
    const [, titleTemplate] = parseReportTitleParts(title);
    if (!templateLabel && titleTemplate) {
      templateLabel = titleTemplate;
    }

    if (!target) {
      target = extractTargetFromReportTitle(title, templateLabel);
    }

    for (const key of ['subject', 'target_name', 'target_audience']) {
      const payloadTarget = String(reportPayload[key] || '').trim();
      if (!target && isUsableTarget(payloadTarget, templateLabel)) {
        target = payloadTarget;
      }
    }
  }

  return {
    clientName: client,
    reportTemplateLabel: templateLabel,
    classification: resolvedClassification,
    lastNameSubject: formatLastNameSubject(target),
  };
};

export const buildExportFilename = ({
  clientName = '',
  reportTemplateLabel = '',
  classification = '',
  customClassification = '',
  lastNameSubject = '',
  targetName = '',
  extension = 'docx',
  reportPayload = null,
  projectName = '',
}) => {
  const resolved = resolveExportFilenameParts({
    clientName,
    projectName,
    reportTemplateLabel,
    classification,
    customClassification,
    reportPayload,
  });
  const client = resolved.clientName || 'Client';
  const template = resolved.reportTemplateLabel || 'Report';
  const marking = resolved.classification || 'UNCLASSIFIED';
  const subject = lastNameSubject || targetName || resolved.lastNameSubject || 'Subject';
  const ext = String(extension || 'docx').replace(/^\./, '').toLowerCase();
  return `${client}-${template}-${marking}-${subject}.${ext}`;
};

/** Parse filename from Content-Disposition (ASCII or RFC 5987 filename*). */
export const parseContentDispositionFilename = (disposition) => {
  const header = String(disposition || '');
  if (!header) return '';

  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch {
      return utfMatch[1].trim();
    }
  }

  const asciiMatch = header.match(/filename="([^"]+)"/i) || header.match(/filename=([^;]+)/i);
  return asciiMatch?.[1]?.trim() || '';
};
